// src/pages/Login.jsx 
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react'; // ADD Eye, EyeOff
import { Button } from '@/components/ui/button.jsx';
import { Input, Label } from '@/components/ui/input.jsx';
import { apiCall } from '../utils/api';
import { afterSignInPath, classifyLoginResponse, saveSession } from '../lib/mfa';
import LoginMfaStep from './LoginMfaStep';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); // ADD THIS
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Set when the password was right and a second factor is on:
  // { value, issuedAt }. Held in memory only, never in storage.
  const [challenge, setChallenge] = useState(null);

  // Never log the response, the token or the stored copy of it: the token
  // is a working login credential, and anything in the console is readable
  // by browser extensions and captured by error-reporting tools.
  const finishSignIn = (data, path) => {
    if (saveSession(data, localStorage)) {
      navigate(path, { replace: true, state: data.recovery_code_used
        ? { recoveryCodeUsed: true, recoveryCodesRemaining: data.recovery_codes_remaining }
        : undefined });
    } else {
      setChallenge(null);
      setError('Login succeeded but session save failed. Please try again.');
    }
  };

  const handleStartOver = (message) => {
    setChallenge(null);
    setPassword('');
    setError(message || '');
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsLoading(true);
  setError('');

  try {
    const res = await apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });

    if (res.ok) {
      const data = await res.json();
      const outcome = classifyLoginResponse(data);

      if (outcome.kind === 'mfa') {
        setChallenge({ value: outcome.challenge, issuedAt: Date.now() });
        setPassword('');
      } else if (outcome.kind === 'session') {
        finishSignIn(data, '/');
      } else {
        setError('Sign-in returned something unexpected. Please try again.');
      }

    } else {
      // Handle login failure
      const errorData = await res.json().catch(() => ({}));
      setError(errorData.detail || 'Invalid email or password');
    }
  } catch (err) {
    console.error('Login error:', err);
    setError('Network error. Please check your connection and try again.');
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] p-4">
      {/* Auth Card */}
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src="/connexlogo2-transparent.png" 
            alt="Connex" 
            className="h-16 w-auto"
          />
        </div>

        {/* Card */}
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-white mb-2">
              {challenge ? 'Two-step verification' : 'Welcome back'}
            </h1>
            <p className="text-sm text-[#a0a0a0]">
              {challenge
                ? `One more step to sign in as ${email}`
                : 'Sign in to your account to continue'}
            </p>
          </div>

          {challenge ? (
            <LoginMfaStep
              challenge={challenge}
              onSignedIn={(body) => finishSignIn(body, afterSignInPath(body))}
              onStartOver={handleStartOver}
            />
          ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              </div>
            )}

            {/* Email */}
            <div className="space-y-2">
              <Label>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password - UPDATED */}
            <div className="space-y-2">
              <Label>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6a6a6a] hover:text-white transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
          )}

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-[#6a6a6a]">
              {challenge
                ? 'Lost your authenticator and your recovery codes? Contact Connex support.'
                : 'Need access? Contact your administrator'}
            </p>
          </div>
        </div>

        {/* Bottom Text */}
        <p className="text-center text-xs text-[#6a6a6a] mt-8">
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}